<template>
  <div>
    <app-information-msg-component
        v-if="informations.length !== 0"
            :title="'detailFacture.messages.info.information'"
            :libelleList="informations"
            :canClose="true"
    >
    </app-information-msg-component>
    <div v-if="detailDevisCommande"
         class="is-flex pdt-20px columns is-multiline">
      <div class="corps column is-10">
        <qd-message
            v-if="!isSyncRegleWithLignes && modulePresseActive"
            type="is-warning"
            aria-close-label="Close message">
            {{ $t('editionCommande.informations.regleFacturation.erreur') }}
        </qd-message>
        <user-warning-message
          :users="[
            { id: devisCommande.commercialId, msg: 'utilisateur.warning.commercialArchive' },
            { id: devisCommande.assistantId, msg: 'utilisateur.warning.assistantArchive' },
          ]"/>
        <contact-warning-message :contacts="archivedContacts"/>
        <div class="steps-detail" v-if="devisCommande.sens === SENS_ACHAT">
          <dot-steps :steps="commandSteps"
                     :current-step="detailDevisCommande.etat"/>
        </div>
        <qd-notification ref="element" :closable="false" v-else
                        class="has-background-background is-paddingless">
          <stepper  :steps="tabFilArianeDcf"
                    :last-active-step="getLastActiveStep"
                    :actual-step="getActualStep">
          </stepper>
        </qd-notification>
        <facture-information :facture="detailDevisCommande"
                             :factureDetail="devisCommande"/>
      </div>
      <div class="column is-2">
        <div class="sticky-20px">
          <dots-menu :steps="steps">
          </dots-menu>
          <information-create-update-user :data="devisCommande">
          </information-create-update-user>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import AppInformationMsgComponent from '@/components/general/informationMsgComponent';
import DotsMenu from '@/components/general/dotsMenu';
import DotSteps from '@/components/general/dotSteps';
import factureInformation from '@/components/factures/factureInformation';
import { SENS_ACHAT } from '@/configuration/devisConf';
import stepper from '@/components/devisCommandeFacture/detail/stepper/stepper';
import { SOUS_TYPE_AVOIR, SOUS_TYPE_AVOIR_SUR_ACOMPTE } from '@/configuration/facture/factureConf';
import createFilArianeFacture from '@/services/dcf/detail/filAriane/createFilArianeFacture';
import InformationCreateUpdateUser from '@/components/general/informationCreateUpdateUser';
import userWarningMessage from '@/components/commun/user/userWarningMessage';
import contactWarningMessage from '@/components/commun/tiers/contact/contactWarningMessage';
import MixinParametreInterne from '@/mixins/parametreInterne';
import { PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';
import messageErreur from '@/services/general/messageErreur';
import getArchivedContactsFromIntervenants from '@/services/contact/getArchivedContactsFromIntervenants';
import { MODULE_TLPE } from '@/configuration/gabed/titreRecette/titreRecetteConf';

const Parametres = createNamespacedHelpers('parametres');

export default {
  components: {
    DotSteps,
    DotsMenu,
    factureInformation,
    AppInformationMsgComponent,
    stepper,
    InformationCreateUpdateUser,
    userWarningMessage,
    contactWarningMessage,
  },
  props: {
    detailDevisCommande: Object,
    devisCommande: Object,
  },
  mixins: [MixinParametreInterne],
  computed: {
    payeur() {
      return this.devisCommande.intervenants.find((int) => { return int.payeur; });
    },
    getLastActiveStep() {
      const tabFilArianceDcfCloneDeep = cloneDeep(this.tabFilArianeDcf);
      tabFilArianceDcfCloneDeep.splice(0, 2);
      const indexFirstStepNotActive = tabFilArianceDcfCloneDeep.findIndex((filAriane) => {
        return !filAriane.isActive;
      });
      return indexFirstStepNotActive === -1 ? this.tabFilArianeDcf.length : indexFirstStepNotActive;
    },
    getActualStep() {
      if ([SOUS_TYPE_AVOIR, SOUS_TYPE_AVOIR_SUR_ACOMPTE]
        .includes(this.devisCommande.sousType)) return 3;
      return 2;
    },
    isSyncRegleWithLignes() {
      if (this.devisCommande.regleFacturationId) {
        return this.devisCommande.lignesCommande.length > 0
          && this.devisCommande.lignesCommande.some(ligne => {
            return ligne.produit
              && (ligne.produit.regleFacturationId === this.devisCommande.regleFacturationId);
          });
      }
      return true;
    },
  },
  data() {
    return {
      informations: [],
      commandSteps: [
        'Brouillon',
        'Enregistre',
        'Paye',
        'Annule',
      ],
      steps: [
        {
          name: this.devisCommande.module !== MODULE_TLPE ? this.$i18n.t('detailFacture.anchor.title')
            : this.$i18n.t('detailTitreRecette.onglet.titreRecette'),
          ref: '#detail-command',
          active: true,
        },
        {
          name: this.$i18n.t('detailFacture.anchor.echeancierPaiement'),
          ref: '#echeancierPaiement',
          active: false,
        },
        {
          name: this.$i18n.t('detailFacture.anchor.comments'),
          ref: '#detail-comments',
          active: false,
        },
        {
          name: this.$i18n.t('detailFacture.anchor.avoirLie'),
          ref: '#avoir-lie',
          active: false,
        },
        {
          name: this.$i18n.t('detailFacture.anchor.piecesJointes'),
          ref: '#attachments',
          active: false,
        },
      ],
      SENS_ACHAT: SENS_ACHAT,
      tabFilArianeDcf: [],
      isLoading: false,
      archivedContacts: [],
    };
  },
  methods: {
    ...Parametres.mapActions({
      loadParametresInterne: 'loadParametresInterne',
    }),
    setInformations() {
      const informations = [];
      if (this.payeur.tiers.factureParMail) {
        informations.push({
          isLink: false,
          libelle: this.$i18n.t('detailFacture.messages.info.factureParMail',
            { nom: this.payeur.nomCommercial }),
        });
      }
      if (this.payeur.tiers.instructionDematerialisation) {
        informations.push({
          isLink: false,
          libelle: this.$i18n.t('detailFacture.messages.info.instructionsDemat',
            { instructions: this.payeur.tiers.instructionDematerialisation }),
        });
      }
      this.informations = informations;
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      if (this.devisCommande.sens === SENS_ACHAT) {
        this.steps.slice(3, 1);
      } else {
        createFilArianeFacture(this.devisCommande).then(rsltTabAriane => {
          this.tabFilArianeDcf = rsltTabAriane;
        }).catch(e => {
          loadingComponent.close();
          throw e;
        });
      }
      await this.loadParametresInterne([
        PARAMETRES_INTERNE_KEYS.modulePresse.key,
      ]);
      loadingComponent.close();
    } catch (e) {
      loadingComponent.close();
      messageErreur(this.$i18n);
      throw e;
    }
    this.setInformations();
    this.archivedContacts = getArchivedContactsFromIntervenants(this.devisCommande.intervenants);
  },
};
</script>
